import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramPumpEfficiency } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.hydronicPumpEff.v1';

type Fields = {
  waterHorsepower: string;
  brakeHorsepower: string;
  pumpEfficiency: string;
};
const EMPTY: Fields = { waterHorsepower: '', brakeHorsepower: '', pumpEfficiency: '' };

/**
 * Hydronics — Pump Efficiency
 *   Efficiency = (WHP / BHP) × 100
 * Solves for any missing field when the other 2 are supplied.
 */
const HydronicPumpEfficiencyScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const whp = toImp(fields.waterHorsepower, 'power');
    const bhp = toImp(fields.brakeHorsepower, 'power');
    const eff = parseFloat(fields.pumpEfficiency);

    if (fields.pumpEfficiency === '') {
      // Eff = (WHP / BHP) × 100
      if (!(bhp > 0)) {
        setError('Brake horsepower must be greater than zero.');
        return;
      }
      const calcEff = (whp / bhp) * 100;
      if (calcEff > 100) {
        setError('Calculated efficiency exceeds 100%. Please check your values.');
        return;
      }
      setSolvedFor('pumpEfficiency');
      set({ pumpEfficiency: calcEff.toFixed(1) });
    } else if (fields.waterHorsepower === '') {
      // WHP = (BHP × Eff) / 100
      if (eff < 0 || eff > 100) {
        setError('Pump efficiency must be between 0 and 100%.');
        return;
      }
      setSolvedFor('waterHorsepower');
      set({ waterHorsepower: fromImp((bhp * eff) / 100, 'power', 3) });
    } else if (fields.brakeHorsepower === '') {
      // BHP = WHP / (Eff / 100)
      if (!(eff > 0) || eff > 100) {
        setError('Pump efficiency must be between 0 and 100%.');
        return;
      }
      setSolvedFor('brakeHorsepower');
      set({ brakeHorsepower: fromImp(whp / (eff / 100), 'power', 3) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.waterHorsepower && { label: `Water ${unit('power')}`, value: fields.waterHorsepower, unit: unit('power'), emphasis: solvedFor === 'waterHorsepower' },
    fields.brakeHorsepower && { label: `Brake ${unit('power')}`, value: fields.brakeHorsepower, unit: unit('power'), emphasis: solvedFor === 'brakeHorsepower' },
    fields.pumpEfficiency && { label: 'Pump Efficiency', value: fields.pumpEfficiency, unit: '%', emphasis: solvedFor === 'pumpEfficiency' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Pump Efficiency"
      subtitle="Calculate pump efficiency based on water horsepower and brake horsepower"
      formula={
        <>
          <FormulaLine>Efficiency = (WHP / BHP) × 100</FormulaLine>
          <FormulaNote>
            Efficiency = Pump Efficiency (%), WHP = Water {unit('power')}, BHP = Brake {unit('power')}.
            Solves for any variable when the other two are provided.
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Efficiency = (WHP / BHP) × 100"
      diagram={<DiagramPumpEfficiency />}
      outputSlots={{
        water_hp: fields.waterHorsepower ? toImp(fields.waterHorsepower, 'power') : undefined,
        bhp: fields.brakeHorsepower ? toImp(fields.brakeHorsepower, 'power') : undefined,
        pump_efficiency_pct: fields.pumpEfficiency ? parseFloat(fields.pumpEfficiency) : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.water_hp != null && !fields.waterHorsepower) {
          patch.waterHorsepower = fromImp(values.water_hp, 'power', 3);
        }
        if (values.bhp != null && !fields.brakeHorsepower) {
          patch.brakeHorsepower = fromImp(values.bhp, 'power', 3);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Water ${unit('power')}`}
          value={fields.waterHorsepower}
          onChangeText={(v) => set({ waterHorsepower: v })}
          rightText={unit('power')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Brake ${unit('power')}`}
          value={fields.brakeHorsepower}
          onChangeText={(v) => set({ brakeHorsepower: v })}
          rightText={unit('power')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label="Pump Efficiency (%)"
          value={fields.pumpEfficiency}
          onChangeText={(v) => set({ pumpEfficiency: v })}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default HydronicPumpEfficiencyScreen;
